import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Shipments } from 'src/app/models/shipment';
import { OrdersService } from 'src/app/services/orders.service';
import { Log } from 'src/app/services/shared/log';
import { environment } from 'src/environments/environment';

/**
 * Shipment tracking widget — used inside the modal opened from
 * `<app-agente-list>` and `<app-client-list>` row 📍 actions.
 *
 * Rewritten 2026-06-09 (v2.4.0 prep). The previous implementation had:
 *  - a `FormBuilder` with 12 form controls **none of which were ever
 *    bound to anything in the template** — pure dead code,
 *  - a `kendo-dialog` wrapper with a red close button duplicating the
 *    parent modal chrome,
 *  - the project's `CLIENT_TOKEN` *hardcoded as a plaintext string
 *    literal in the TS* (we already cleaned up the analogous case in
 *    the details widget for Phase 0 / T1; this one survived),
 *  - six `console.log` calls including one with an Italian/Romanian
 *    curse word that somehow shipped to production.
 *
 * The new component drops all of the above and:
 *  - keeps the three backend paths
 *    (`getClientShipmentStatus` / `getShippmentById` / `getShippmentById1`),
 *  - exposes the shipment status as a normalised list of "steps" the
 *    template renders as a vertical timeline,
 *  - relies on `environment.CLIENT_TOKEN` instead of a literal,
 *  - lets the parent modal own the close gesture (no
 *    `OpenModalService` dependency).
 *
 * The widget reloads on input change (`ngOnChanges`) so the parent
 * can reuse a single instance across multiple tracking opens without
 * destroy/recreate cycles.
 */
@Component({
  selector: 'app-accesso-funzionari-tracking',
  templateUrl: './accesso-funzionari-tracking.component.html',
  styleUrls: ['./accesso-funzionari-tracking.component.scss'],
  standalone: false,
})


export class AccessoFunzionariTrackingComponent implements OnInit, OnChanges {

  private readonly ordersServ = inject(OrdersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // ── Inputs (from parent modal) ──────────────────────────────────
  /** Shipment tracking ID (passed by the list pages). */
  @Input() track_ID: string | number = '';
  /** Token — admin agent token, or `environment.CLIENT_TOKEN` for
   *  client-facing flows. */
  @Input() token = '';
  /** Alternative entry point: lookup by raw shipment id. */
  @Input() shipment_id: number | string | null = null;
  /** Agent ID (used by the agent-token path). */
  @Input() agentID: string | number = '';
  /** Legacy flags — kept for backwards-compat with the two parent
   *  templates; their value drives the `@if` in the markup. */
  @Input() openTrack = false;
  @Input() newTrack = false;

  // ── State ───────────────────────────────────────────────────────
  shipment: Shipments.ShipmentItem | null = null;
  loading = true;
  private lastLoadedKey = '';

  // ── Lifecycle ───────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadShipment();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldReload =
      changes['track_ID']?.currentValue !== undefined ||
      changes['shipment_id']?.currentValue !== undefined ||
      changes['token']?.currentValue !== undefined ||
      changes['agentID']?.currentValue !== undefined;

    if (shouldReload) {
      this.loadShipment();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Status label for the badge in the header. */
  get statusVariant(): 'delivered' | 'exception' | 'missed' | 'transit' | 'received' {
    switch (this.shipment?.sm_status_cod) {
      case 6:
        return 'delivered';
      case 5:
        return 'exception';
      case 4:
        return 'missed';
      case 1:
        return 'received';
      default:
        return 'transit';
    }
  }

  /**
   * Emoji rendered for a single timeline step. Falls back to ⏺ when
   * the backend `StatusCode` is unknown so the timeline doesn't break.
   */
  iconForStep(statusCode: number | undefined | null): string {
    switch (statusCode) {
      case 1: return '📥'; // info received
      case 2: return '🚚'; // in transit
      case 3: return '🏃'; // out for delivery
      case 4: return '⏰'; // missed delivery
      case 5: return '⚠️'; // exception
      case 6: return '✅'; // delivered
      default: return '⏺';
    }
  }

  /** Big banner state derived from the shipment-level status code. */
  get bannerVariant(): 'success' | 'warning' | 'danger' | 'info' {
    switch (this.shipment?.sm_status_cod) {
      case 6:
        return 'success';   // delivered
      case 4:
        return 'warning';   // missed delivery
      case 5:
        return 'danger';    // exception
      default:
        return 'info';      // in progress
    }
  }

  /** Empty when there are no steps to render — drives the empty state. */
  get hasSteps(): boolean {
    return !!this.shipment?.DetailsIcons?.length;
  }

  /** Open the carrier's external tracking URL in a new tab. */
  openExternalLink(url: string | undefined | null): void {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader (3 backend paths preserved unchanged).
  // ──────────────────────────────────────────────────────────────────

  private loadShipment(): void {
    const key = `${this.track_ID}|${this.shipment_id}|${this.token}`;

    if (key === this.lastLoadedKey) return;
    this.lastLoadedKey = key;

    this.shipment = null;
    this.loading = true;
    this.cdr.markForCheck();

    const isClientToken =
      !!this.token && this.token === environment.CLIENT_TOKEN;

    if (isClientToken && this.track_ID) {
      this.fetchClientShipment();
      return;
    }

    if (this.trackIdNum !== null) {
      this.fetchByTrackId();
      return;
    }

    if (this.shipment_id != null && this.shipment_id !== '') {
      this.fetchByShipmentId();
      return;
    }

    this.loading = false;
    this.cdr.markForCheck();
  }

  // Service signatures want numeric `orderId` / `agent_id`. Coerce at
  // the call site (we keep the inputs loosely typed because the parent
  // sometimes hands us strings from query params).
  //
  // 🚨 FIX v2.11.2 (Q1b): legacy ritornava `0` quando track_ID era
  // stringa vuota o non valorizzato (`Number('') === 0` e
  // `Number.isFinite(0) === true`). Il caller `loadShipment()` faceva
  // `if (this.trackIdNum !== null)` che passava con 0 → chiamava
  // `/get_agent_shipments_status/0/0` → BE rispondeva con msg_code 7
  // (token scaduto/non valido) → AuthInterceptor logout → utente
  // sbattuto al login. Succedeva quando il parent (status-spedizioni)
  // passava solo `shipment_id` e lasciava `track_ID = ''` (default).
  // Ora trattiamo `''` / `0` / `null` come "non valido" → fallback a
  // `fetchByShipmentId()` che usa il valore reale.
  private get trackIdNum(): number | null {
    if (this.track_ID === '' || this.track_ID == null) return null;
    const n = Number(this.track_ID);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  private get agentIdNum(): number | null {
    if (this.agentID === '' || this.agentID == null) return null;
    const n = Number(this.agentID);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private fetchClientShipment(): void {
    this.ordersServ
      .getClientShipmentStatus(this.token, this.trackIdNum, this.agentIdNum)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => this.handleShipment(data),
        error: (err) => this.handleError('getClientShipmentStatus', err),
      });
  }

  private fetchByTrackId(): void {
    if (this.trackIdNum === null) {
      this.loading = false;
      return;
    }
    this.ordersServ
      .getShippmentById(this.token, this.trackIdNum, this.agentIdNum)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => this.handleShipment(data),
        error: (err) => this.handleError('getShippmentById', err),
      });
  }

  private fetchByShipmentId(): void {
    const id = Number(this.shipment_id);
    if (!Number.isFinite(id)) return;
    this.ordersServ
      .getShippmentById1(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => this.handleShipment(data),
        error: (err) => this.handleError('getShippmentById1', err),
      });
  }

  /**
   * 🚨 FIX v2.12.0 (L.2): `Shipments.ShipmentItem(null)` ritorna un'istanza
   * TRUTHY con tutti i field empty (constructor `if (!shipment) return;`).
   * Per ordini storici senza tracking (consegnati a mano, pre-tracking-
   * system, ecc.) il BE risponde `data: null` → service crea ShipmentItem
   * vuoto → `!shipment` nel template era false → render banner+grid pieni
   * di "—" em-dash invece dell'empty state friendly. Ora trattiamo come
   * "nessun dato" se mancano sia tracking_number che status_cod che steps
   * — segnali minimi che il record sia popolato realmente.
   */
  private handleShipment(data: Shipments.ShipmentItem | null | undefined): void {
    const hasData =
      !!data &&
      (
        !!data.sm_tracking_number ||
        !!data.sm_status_cod ||
        !!data.sm_status_desc ||
        !!data.or_order_number ||
        !!data.DetailsIcons?.length
      );
    this.shipment = hasData ? (data as Shipments.ShipmentItem) : null;
    this.loading = false;
    this.cdr.markForCheck();
  }

  private handleError(endpoint: string, err: unknown): void {
    Log.error(`[AccessoFunzionariTracking] ${endpoint} failed`, err);
    this.loading = false;
    this.cdr.markForCheck();
  }
  
}
